// useLocalStorage Hook - Local Storage State Management
import { useState, useCallback, useEffect } from 'react';

/**
 * Local storage hook that syncs state with localStorage
 *
 * @param {string} key - The localStorage key
 * @param {*} initialValue - Initial value if key doesn't exist
 * @param {Object} options - Configuration options
 * @param {boolean} options.serialize - Whether to serialize/deserialize values
 * @param {Function} options.onError - Error handler
 * @returns {Array} [value, setValue, removeValue]
 */
export const useLocalStorage = (key, initialValue, options = {}) => {
  const { serialize = true, onError = (error) => console.error('useLocalStorage error:', error) } =
    options;

  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (typeof window === 'undefined') {
        return initialValue;
      }

      const item = window.localStorage.getItem(key);
      if (item === null) {
        return initialValue;
      }

      return serialize ? JSON.parse(item) : item;
    } catch (error) {
      onError(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback(
    (value) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          if (valueToStore === undefined) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(
              key,
              serialize ? JSON.stringify(valueToStore) : valueToStore
            );
          }
        }
      } catch (error) {
        onError(error);
      }
    },
    [key, serialize, storedValue, onError]
  );

  // Remove value from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      onError(error);
    }
  }, [key, initialValue, onError]);

  // Listen for changes to this localStorage key from other tabs/windows
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = serialize ? JSON.parse(e.newValue) : e.newValue;
          setStoredValue(newValue);
        } catch (error) {
          onError(error);
        }
      } else if (e.key === key && e.newValue === null) {
        setStoredValue(initialValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue, serialize, onError]);

  return [storedValue, setValue, removeValue];
};

/**
 * Session storage hook that syncs state with sessionStorage
 *
 * @param {string} key - The sessionStorage key
 * @param {*} initialValue - Initial value if key doesn't exist
 * @param {Object} options - Configuration options
 * @returns {Array} [value, setValue, removeValue]
 */
export const useSessionStorage = (key, initialValue, options = {}) => {
  const {
    serialize = true,
    onError = (error) => console.error('useSessionStorage error:', error),
  } = options;

  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (typeof window === 'undefined') {
        return initialValue;
      }

      const item = window.sessionStorage.getItem(key);
      if (item === null) {
        return initialValue;
      }

      return serialize ? JSON.parse(item) : item;
    } catch (error) {
      onError(error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          if (valueToStore === undefined) {
            window.sessionStorage.removeItem(key);
          } else {
            window.sessionStorage.setItem(
              key,
              serialize ? JSON.stringify(valueToStore) : valueToStore
            );
          }
        }
      } catch (error) {
        onError(error);
      }
    },
    [key, serialize, storedValue, onError]
  );

  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(key);
      }
    } catch (error) {
      onError(error);
    }
  }, [key, initialValue, onError]);

  return [storedValue, setValue, removeValue];
};

/**
 * Persistent state hook that automatically saves to localStorage
 *
 * @param {string} key - The storage key
 * @param {*} initialValue - Initial value
 * @param {Object} options - Configuration options
 * @returns {Array} [state, setState, clearState]
 */
export const usePersistentState = (key, initialValue, options = {}) => {
  const [value, setValue, removeValue] = useLocalStorage(key, initialValue, options);

  return [value, setValue, removeValue];
};

/**
 * Storage quota hook to check storage usage and limits
 *
 * @returns {Object} Storage quota information
 */
export const useStorageQuota = () => {
  const [quota, setQuota] = useState({
    used: 0,
    total: 0,
    available: 0,
    percentage: 0,
  });

  const updateQuota = useCallback(async () => {
    if (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'estimate' in navigator.storage
    ) {
      try {
        const estimate = await navigator.storage.estimate();
        const used = estimate.usage || 0;
        const total = estimate.quota || 0;
        const available = total - used;
        const percentage = total > 0 ? (used / total) * 100 : 0;

        setQuota({
          used,
          total,
          available,
          percentage,
        });
      } catch (error) {
        console.error('Failed to estimate storage:', error);
      }
    }
  }, []);

  useEffect(() => {
    updateQuota();
  }, [updateQuota]);

  return {
    ...quota,
    refresh: updateQuota,
    isLow: quota.percentage > 80,
    isFull: quota.percentage > 95,
  };
};

/**
 * Storage event hook to listen for storage changes
 *
 * @param {string} key - The storage key to listen for
 * @param {Function} callback - Callback function when storage changes
 * @param {string} storageType - 'localStorage' or 'sessionStorage'
 */
export const useStorageEvent = (key, callback, storageType = 'localStorage') => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e) => {
      if (e.key === key) {
        callback(e.newValue, e.oldValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, callback]);
};

export default useLocalStorage;
